export const cardSurface =
  'rounded-xl border border-primary-500/20 bg-gradient-to-br from-primary-50/50 via-white to-white dark:from-primary-900/30 dark:via-gray-800/90 dark:to-gray-800 shadow-sm ring-1 ring-primary-500/10 dark:ring-primary-400/20'

export const feedCardClass =
  'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden'

export const dashboardListClass =
  'rounded-lg border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 overflow-hidden bg-white dark:bg-gray-800'

export const dashboardInsetPanelClass =
  'rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'

export const commentBubbleClass =
  'rounded-2xl bg-gray-100 dark:bg-gray-700/70 px-3 py-2'

export function formatSocialDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min atrás`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} h atrás`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} d atrás`

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function getInitials(name?: string | null): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export function getAvatarColor(name?: string | null): string {
  const palette = [
    'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  ]
  const seed = (name || '?').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return palette[seed % palette.length]
}

export function getPostInteractionCount(post: {
  reactions?: unknown[] | null
  comments?: unknown[] | null
  comment_count?: number | null
}): number {
  const reactions = post.reactions?.length ?? 0
  const comments = post.comments?.length ?? post.comment_count ?? 0
  return reactions + comments
}

export function truncatePostContent(content: string, maxLength = 80): string {
  const singleLine = content.replace(/\s+/g, ' ').trim()
  if (singleLine.length <= maxLength) return singleLine
  return `${singleLine.slice(0, maxLength)}…`
}

export function getRankBadgeClass(rank: number): string {
  if (rank === 1) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
  if (rank === 2) return 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
  if (rank === 3) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
  return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
}

export interface ActiveUserStats {
  userId: string
  name?: string | null
  avatar_url?: string | null
  posts: number
  comments: number
  reactions: number
  total: number
}

export function getTopActiveUsers(
  posts: Array<{
    author_id: string
    author?: { id?: string; name?: string | null; avatar_url?: string | null } | null
    comments?: Array<{
      author_id: string
      author?: { name?: string | null; avatar_url?: string | null } | null
    }> | null
    reactions?: Array<{
      user_id: string
      user?: { name?: string | null; avatar_url?: string | null } | null
    }> | null
  }>,
  limit = 5
): ActiveUserStats[] {
  const map = new Map<string, ActiveUserStats>()

  const ensure = (user: {
    id: string
    name?: string | null
    avatar_url?: string | null
  }): ActiveUserStats => {
    let entry = map.get(user.id)
    if (!entry) {
      entry = {
        userId: user.id,
        name: user.name,
        avatar_url: user.avatar_url,
        posts: 0,
        comments: 0,
        reactions: 0,
        total: 0,
      }
      map.set(user.id, entry)
    } else {
      if (!entry.name && user.name) entry.name = user.name
      if (!entry.avatar_url && user.avatar_url) entry.avatar_url = user.avatar_url
    }
    return entry
  }

  for (const post of posts) {
    const postAuthor = ensure({
      id: post.author_id,
      name: post.author?.name,
      avatar_url: post.author?.avatar_url,
    })
    postAuthor.posts += 1
    postAuthor.total += 1

    for (const comment of post.comments || []) {
      const commentAuthor = ensure({
        id: comment.author_id,
        name: comment.author?.name,
        avatar_url: comment.author?.avatar_url,
      })
      commentAuthor.comments += 1
      commentAuthor.total += 1
    }

    for (const reaction of post.reactions || []) {
      const reactionUser = ensure({
        id: reaction.user_id,
        name: reaction.user?.name,
        avatar_url: reaction.user?.avatar_url,
      })
      reactionUser.reactions += 1
      reactionUser.total += 1
    }
  }

  return [...map.values()]
    .filter((user) => user.total > 0)
    .sort((a, b) => b.total - a.total || b.posts - a.posts || b.comments - a.comments)
    .slice(0, limit)
}
